import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Users, Building, Lightbulb, Sprout, Heart, Handshake, TrendingUp, Shield, Award } from 'lucide-react';

export const Impact = () => {
  const socioEconomicImpact = [
    {
      icon: Users,
      title: 'Création d\'Emplois',
      stats: '500+ emplois directs d\'ici 2030',
      details: [
        '2 000+ emplois indirects',
        'Formation continue',
        'Promotion interne'
      ],
      color: '#FF6600'
    },
    {
      icon: Building,
      title: 'Développement d\'Infrastructures',
      stats: '120M USD investis',
      details: [
        'Infrastructures pétrolières',
        '179+ hectares immobilier',
        '50+ stations-services modernes',
        'Standards internationaux'
      ],
      color: '#003366'
    },
    {
      icon: Lightbulb,
      title: 'Innovation & Technologie',
      stats: 'Transfert de technologies',
      details: [
        'Technologies internationales adaptées',
        'Digitalisation (KIPAY, domotique, IoT)',
        'Montée en compétences'
      ],
      color: '#00A651'
    }
  ];

  const rseCommitments = [
    {
      category: 'Environnement',
      icon: Sprout,
      color: '#00A651',
      commitments: [
        { title: 'Normes environnementales strictes', desc: 'Certification ISO 14001' },
        { title: 'Efficacité énergétique', desc: 'Dans toutes les constructions' },
        { title: 'Réduction empreinte carbone', desc: 'Objectifs mesurables' },
        { title: 'Gestion responsable', desc: 'Des ressources naturelles' }
      ]
    },
    {
      category: 'Social',
      icon: Heart,
      color: '#FF6600',
      commitments: [
        { title: 'Programme de formation', desc: 'Pour les jeunes talents' },
        { title: 'Inclusion', desc: '30% de femmes dans les équipes d\'ici 2027' },
        { title: 'Santé & sécurité', desc: 'Objectif zéro accident' },
        { title: 'Dialogue social', desc: 'Constructif et transparent' }
      ]
    },
    {
      category: 'Communautaire',
      icon: Handshake,
      color: '#003366',
      commitments: [
        { title: 'Développement local', desc: 'Priorité aux fournisseurs ivoiriens' },
        { title: 'Infrastructures partagées', desc: 'Routes, électrification' },
        { title: 'Soutien à l\'éducation', desc: 'Campus universitaire Yamoussoukro' },
        { title: 'Partenariats institutionnels', desc: 'Avec le gouvernement' }
      ]
    }
  ];

  const keyFigures = [
    { icon: Building, value: '4', label: 'Filiales Opérationnelles', color: '#003366' },
    { icon: TrendingUp, value: '120M USD', label: 'Projet Infrastructure', color: '#FF6600' },
    { icon: Building, value: '179+ Ha', label: 'Portfolio Immobilier', color: '#00A651' },
    { icon: Sprout, value: '50+', label: 'Stations Wayne (2030)', color: '#FFB81C' },
    { icon: Users, value: '500+', label: 'Emplois Directs (2030)', color: '#FF6600' },
    { icon: Shield, value: '140 000 m³', label: 'Stockage Pétrolier', color: '#003366' },
    { icon: Building, value: '5', label: 'Sites Majeurs', color: '#00A651' },
    { icon: Award, value: 'National', label: 'Présence Côte d\'Ivoire', color: '#FFB81C' }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#003366] to-[#004080]">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            L'Impact EKIP
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Notre contribution au développement économique, social et environnemental de la Côte d'Ivoire
          </p>
        </div>
      </section>

      {/* Socio-Economic Impact */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#003366] mb-6">
              Notre Impact Socio-Économique
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {socioEconomicImpact.map((impact, index) => {
              const Icon = impact.icon;
              return (
                <Card key={index} className="border-2 hover:shadow-xl transition-all duration-300" style={{ borderColor: impact.color }}>
                  <CardContent className="p-8">
                    <div 
                      className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6"
                      style={{ backgroundColor: `${impact.color}15` }}
                    >
                      <Icon style={{ color: impact.color }} size={32} />
                    </div>
                    <h3 className="text-2xl font-bold text-[#003366] mb-3">{impact.title}</h3>
                    <div className="text-xl font-bold mb-6" style={{ color: impact.color }}>
                      {impact.stats}
                    </div>
                    <ul className="space-y-3">
                      {impact.details.map((detail, i) => (
                        <li key={i} className="flex items-start space-x-3">
                          <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: impact.color }} />
                          <span className="text-gray-700">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* RSE Commitments */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#003366] mb-6">
              Notre Engagement RSE
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Des actions concrètes pour un développement durable
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {rseCommitments.map((category, index) => {
              const Icon = category.icon;
              return (
                <Card key={index} className="border-0 shadow-xl">
                  <CardContent className="p-8">
                    <div className="flex items-center space-x-4 mb-8">
                      <div 
                        className="p-4 rounded-lg"
                        style={{ backgroundColor: category.color }}
                      >
                        <Icon className="text-white" size={28} />
                      </div>
                      <h3 className="text-2xl font-bold text-[#003366]">{category.category}</h3>
                    </div>

                    <div className="space-y-6">
                      {category.commitments.map((commitment, i) => (
                        <div key={i}>
                          <h4 className="font-bold text-[#003366] mb-1">{commitment.title}</h4>
                          <p className="text-gray-600 text-sm">{commitment.desc}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Key Figures */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#003366] to-[#004080]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Notre Groupe en Chiffres
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {keyFigures.map((figure, index) => {
              const Icon = figure.icon;
              return (
                <Card key={index} className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20 transition-all duration-300">
                  <CardContent className="p-6 text-center">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-4" style={{ backgroundColor: figure.color }}>
                      <Icon className="text-white" size={24} />
                    </div>
                    <div className="text-4xl font-bold text-white mb-2">{figure.value}</div>
                    <div className="text-gray-200 text-sm">{figure.label}</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <Card className="border-2 border-[#00A651] shadow-2xl">
            <CardContent className="p-12">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-[#00A651] rounded-full mb-8">
                  <Award className="text-white" size={40} />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-[#003366] mb-6">
                  Notre Vision pour 2030
                </h2>
                <p className="text-xl text-gray-700 leading-relaxed mb-8">
                  Devenir le groupe de référence en Afrique de l'Ouest pour l'écosystème de vie intégré, 
                  créant de la valeur pour nos clients, nos employés, nos partenaires et la société ivoirienne.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-[#00A651] mb-2">Excellence</div>
                    <p className="text-gray-600">Dans chaque réalisation</p>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-[#FF6600] mb-2">Innovation</div>
                    <p className="text-gray-600">Pour l'Afrique de demain</p>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-[#003366] mb-2">Durabilité</div>
                    <p className="text-gray-600">Pour les générations futures</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};